/**
 * use-job-type-customers — JOBTYPE-13 job-type ↔ customer scoping.
 *
 * Scoping is keyed on the job-type FAMILY, not a single version, so it survives
 * a structural edit's version fork (D-01).
 *
 * ALL-CUSTOMERS SENTINEL: an EMPTY customerIds array means the job type is
 * available to EVERY customer. It does NOT mean "no customers". The UI must
 * render the empty state as "All customers" — rendering it as "none" would make
 * every unscoped job type look broken.
 *
 * "Customer" here is the tenant's `hauler_operators` row (billing/customer
 * account, not the E&P "operator" catalog behind `useOperators` /
 * GET /v1/operators). The existing list source for these ids is
 * `useHaulerOperators` (packages/web-dispatch/src/features/sites-operators/hooks.ts,
 * GET /v1/hauler-operators) — reuse that hook to populate a customer picker;
 * do not add a second customer list source.
 */

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { JOB_TYPES_QUERY_KEY, JOB_TYPES_ADMIN_QUERY_KEY } from './use-job-types';

/** Response envelope for GET/PUT /v1/job-types/:id/customers. */
export interface JobTypeCustomersResponse {
  readonly familyId: string;
  readonly customerIds: readonly string[];
  readonly allCustomers: boolean;
}

export const jobTypeCustomersQueryKey = (jobTypeId: number): readonly unknown[] =>
  ['job-types', 'customers', jobTypeId] as const;

/** GET /v1/job-types/:id/customers. Disabled while jobTypeId is null. */
export function useJobTypeCustomers(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobTypeId: number | null,
): ReturnType<typeof useApiQuery<JobTypeCustomersResponse>> {
  return useApiQuery<JobTypeCustomersResponse>(
    apiOpts,
    `/v1/job-types/${String(jobTypeId ?? '')}/customers`,
    {
      queryKey:
        jobTypeId === null
          ? (['job-types', 'customers', 'none'] as const)
          : jobTypeCustomersQueryKey(jobTypeId),
      enabled: jobTypeId !== null,
      staleTime: 30_000,
    },
  );
}

/** PUT /v1/job-types/:id/customers — REPLACE-ALL. Sending [] restores all-customers.
 *  On success, invalidates the read-back key plus every catalog key a scoping
 *  change can affect: the unscoped catalog (editor + New Job default list),
 *  the admin catalog, and every per-customer scoped list
 *  (useJobTypesForCustomer's `['job-types','catalog','customer', ...]` keys) —
 *  missing the customer-scoped keys means the New Job picker keeps showing a
 *  job type the admin just un-scoped. */
export function useReplaceJobTypeCustomers(
  apiOpts: ReturnType<typeof useApiOpts>,
  jobTypeId: number,
): ReturnType<typeof useApiMutation<{ customerIds: readonly string[] }, JobTypeCustomersResponse>> {
  const qc = useQueryClient();
  return useApiMutation<{ customerIds: readonly string[] }, JobTypeCustomersResponse>(
    apiOpts,
    `/v1/job-types/${String(jobTypeId)}/customers`,
    'PUT',
    {
      onSuccess: async () => {
        await Promise.all([
          qc.invalidateQueries({ queryKey: jobTypeCustomersQueryKey(jobTypeId) }),
          qc.invalidateQueries({ queryKey: JOB_TYPES_QUERY_KEY }),
          qc.invalidateQueries({ queryKey: JOB_TYPES_ADMIN_QUERY_KEY }),
          // Prefix invalidation — covers EVERY useJobTypesForCustomer(...) key
          // regardless of which customerId it was scoped to.
          qc.invalidateQueries({ queryKey: ['job-types', 'catalog', 'customer'] }),
        ]);
      },
      onError: () => {
        toast.error('Could not update customer scoping. Please try again.');
      },
    },
  );
}
